// resources/js/Contexts/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const ToastContext = createContext();

function persistErrorNotification(message) {
    try {
        const context = typeof window !== 'undefined' ? window.location.pathname : undefined;
        window.axios?.post('/api/notifications/log-error', {
            title: 'Error',
            message: message || 'An error occurred',
            context,
        }).catch(() => {});
    } catch {
        // Silently ignore
    }
}

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((type, message, duration = 5000) => {
        if (type === 'error') {
            persistErrorNotification(message);
        }

        const id = Date.now() + Math.random();
        const persistent = type === 'error' || type === 'warning';
        const newToast = { id, type, message, duration, persistent };

        setToasts(prev => [...prev, newToast]);

        // error and warning stay until the user manually dismisses
        if (!persistent) {
            setTimeout(() => {
                setToasts(prev => prev.filter(toast => toast.id !== id));
            }, duration);
        }
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {typeof document !== 'undefined' && createPortal(
                <ToastContainer toasts={toasts} removeToast={removeToast} />, 
                document.body
            )}
        </ToastContext.Provider>
    );
};

const ToastContainer = ({ toasts, removeToast }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-[9999] space-y-2 pointer-events-none">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
            ))}
        </div>
    );
};

const ToastItem = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    // Trigger entrance animation after mount
    React.useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const handleRemove = () => {
        setIsLeaving(true);
        setTimeout(() => onRemove(toast.id), 300);
    };

    return (
        <div
            className={`
                max-w-md p-4 rounded-lg shadow-lg border pointer-events-auto
                transform transition-all duration-300 ease-out
                ${isLeaving 
                    ? 'translate-x-full opacity-0 scale-95' 
                    : isVisible 
                    ? 'translate-x-0 opacity-100 scale-100' 
                    : 'translate-x-full opacity-0 scale-95'
                }
                ${toast.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-800 dark:bg-emerald-950 dark:border-emerald-400 dark:text-white'
                    : toast.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/90 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
                    : toast.type === 'warning'
                    ? 'bg-yellow-50 dark:bg-yellow-900/90 border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
                    : 'bg-blue-50 dark:bg-blue-900/90 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                }
            `}
        >
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    {toast.type === 'success' ? (
                        <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    ) : toast.type === 'error' ? (
                        <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : toast.type === 'warning' ? (
                        <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    )}
                </div>
                <div className="ml-3 flex-1">
                    <p className="text-sm font-medium whitespace-pre-line">
                        {toast.message}
                    </p>
                    {toast.persistent && (
                        <p className="text-xs opacity-50 mt-1">Click × to dismiss</p>
                    )}
                </div>
                <button
                    onClick={handleRemove}
                    className="ml-4 flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};